import {
	BackgroundMask,
	GradientMask,
	MaskGroup,
	Mask,
	PeopleMask,
	PeopleMaskPart,
	RadialMask,
	SkyMask,
	SubjectMask,
	BrushMask,
} from "@/core/types"
import { optic, Prism } from "optics-ts"
import { IconName } from "@/components/icons/icons"
import { uuid } from "@/utils/uuid"
import { defaultMaskAdjustments } from "@/core/adjustments"
import { unproject } from "@/core/viewport"
import { mat4, vec4 } from "gl-matrix"

export interface MappedMaskGroup {
	properties: MaskGroup
	lens: Prism<MaskGroup[], any, MaskGroup>
	children: MasksMap
}

export interface MappedMask<T = Mask> {
	properties: T
	lens: Prism<MaskGroup[], any, T>
	group: MappedMaskGroup
}

export type MasksMap = MappedMask[]
export type MaskGroupsMap = MappedMaskGroup[]

const localAdjustmentsLens = optic<MaskGroup[]>()

export const PEOPLE_MASK_PART_LABELS: Record<PeopleMaskPart, string> = {
	entire: "Person",
	facialSkin: "Facial Skin",
	eyebrows: "Eyebrows",
	eyes: "Eyes",
	lips: "Lips",
	clothing: "Clothing",
	bodySkin: "Body Skin",
	hair: "Hair",
}

export const mapMasks = (localAdjustmentsList: MaskGroup[]) => {
	const maskGroupsMap: MaskGroupsMap = []

	localAdjustmentsList.forEach((adjustments, index) => {
		const adjustmentsLens = localAdjustmentsLens.at(index)

		const group: MappedMaskGroup = {
			properties: adjustments,
			lens: adjustmentsLens,
			children: [],
		}

		adjustments.masks.forEach((mask, maskIndex) => {
			group.children.push({
				properties: mask,
				lens: adjustmentsLens.prop("masks").at(maskIndex),
				group: group,
			})
		})

		maskGroupsMap.push(group)
	})

	return maskGroupsMap
}

/**
 * Mask creators
 */

export const createMaskGroup = (properties?: Partial<MaskGroup>): MaskGroup => {
	return {
		id: uuid(),
		name: "Mask 1",
		active: true,
		masks: [],
		...defaultMaskAdjustments,
		...properties,
	}
}

export const createRadialMask = (properties?: Partial<RadialMask>): RadialMask => {
	return {
		type: "radial",

		// Common
		id: uuid(),
		name: "Radial Gradient",
		active: true,
		inverted: false,
		blendMode: "add",

		centerX: 0.5,
		centerY: 0.5,
		radiusX: 0.25,
		radiusY: 0.25,
		angle: 0,
		feather: 0.5,

		...properties,
	}
}

export const createGradientMask = (properties?: Partial<GradientMask>): GradientMask => {
	return {
		type: "gradient",

		// Common
		id: uuid(),
		name: "Linear Gradient",
		active: true,
		inverted: false,
		blendMode: "add",

		startX: 0.5,
		startY: 0.1,
		endX: 0.5,
		endY: 0.9,

		...properties,
	}
}

export const createSubjectMask = (properties?: Partial<SubjectMask>): SubjectMask => {
	return {
		type: "subject",

		// Common
		id: uuid(),
		name: "Subject",
		active: true,
		inverted: false,
		blendMode: "add",

		...properties,
	}
}

export const createSkyMask = (properties?: Partial<SkyMask>): SkyMask => {
	return {
		type: "sky",

		// Common
		id: uuid(),
		name: "Sky",
		active: true,
		inverted: false,
		blendMode: "add",

		...properties,
	}
}

export const createBackgroundMask = (properties?: Partial<BackgroundMask>): BackgroundMask => {
	return {
		type: "background",

		// Common
		id: uuid(),
		name: "Background",
		active: true,
		inverted: false,
		blendMode: "add",

		...properties,
	}
}

export const createPeopleMask = (properties?: Partial<PeopleMask>): PeopleMask => {
	return {
		type: "people",

		// Common
		id: uuid(),
		name: "People",
		active: true,
		inverted: false,
		blendMode: "add",

		...properties,
	}
}

export function createPeopleMasksForSelection(options: {
	selectedPersonIndexes: number[]
	peopleParts?: PeopleMaskPart[]
	numberedNames?: boolean
	properties?: Partial<PeopleMask>
}) {
	const selectedPersonIndexes = [...new Set(options.selectedPersonIndexes)].sort(
		(left, right) => left - right,
	)
	const peopleParts: PeopleMaskPart[] = options.peopleParts?.length
		? options.peopleParts
		: ["entire"]

	if (selectedPersonIndexes.length === 0) {
		return []
	}

	return selectedPersonIndexes.flatMap((personIndex) =>
		peopleParts.map((peoplePart) =>
			createPeopleMask({
				...options.properties,
				name: getPeopleMaskName(personIndex, peoplePart, options.numberedNames),
				personIndex,
				personIndexes: undefined,
				peoplePart,
			}),
		),
	)
}

function getPeopleMaskName(
	personIndex: number,
	peoplePart: PeopleMaskPart,
	numberedName: boolean | undefined,
) {
	const includePersonNumber = numberedName ?? true
	if (peoplePart === "entire") {
		return includePersonNumber ? `Person ${personIndex + 1}` : "Person"
	}

	const partName = PEOPLE_MASK_PART_LABELS[peoplePart]
	return includePersonNumber ? `Person ${personIndex + 1} ${partName}` : partName
}

export function createMaskGroupsForMasks(options: {
	masks: Mask[]
	startIndex: number
	separateGroups?: boolean
}) {
	if (options.separateGroups) {
		return options.masks.map((mask, index) =>
			createMaskGroup({
				name: `Mask ${options.startIndex + index + 1}`,
				masks: [mask],
			}),
		)
	}

	if (options.masks.length === 0) {
		return []
	}

	return [
		createMaskGroup({
			name: `Mask ${options.startIndex + 1}`,
			masks: options.masks,
		}),
	]
}

export const createBrushMask = (properties?: Partial<BrushMask>): BrushMask => {
	return {
		type: "brush",

		// Common
		id: uuid(),
		name: "Brush",
		active: true,
		inverted: false,
		blendMode: "add",

		strokes: [],

		...properties,
	}
}

/**
 * Transform helpers
 */

export const transformMask = (mask: Mask, invMatrix: mat4, crop: vec4, rotation: number) => {
	switch (mask.type) {
		case "radial":
			{
				// Center

				const transformedCenter = unproject(
					[1.0, 1.0],
					[mask.centerX, mask.centerY],
					invMatrix,
				)

				transformedCenter[0] = transformedCenter[0] * 0.5 + 0.5
				transformedCenter[1] = transformedCenter[1] * 0.5 + 0.5

				mask.centerX = transformedCenter[0]
				mask.centerY = transformedCenter[1]

				// Radius

				mask.radiusX *= crop[2]
				mask.radiusY *= crop[3]

				// Angle

				mask.angle = rotation
			}
			break

		case "gradient":
			{
				// Center

				const transformedStart = unproject(
					[1.0, 1.0],
					[mask.startX, mask.startY],
					invMatrix,
				)

				// End

				const transformedEnd = unproject([1.0, 1.0], [mask.endX, mask.endY], invMatrix)

				transformedStart[0] = transformedStart[0] * 0.5 + 0.5
				transformedStart[1] = transformedStart[1] * 0.5 + 0.5

				transformedEnd[0] = transformedEnd[0] * 0.5 + 0.5
				transformedEnd[1] = transformedEnd[1] * 0.5 + 0.5

				mask.startX = transformedStart[0]
				mask.startY = transformedStart[1]

				mask.endX = transformedEnd[0]
				mask.endY = transformedEnd[1]
			}
			break
	}

	return mask
}

/**
 * UI helpers
 */

export const getMaskIcon = (type: Mask["type"]): IconName => {
	switch (type) {
		case "gradient":
			return "gradient_mask"
		case "radial":
			return "radial_mask"
		case "subject":
			return "subject_mask"
		case "background":
			return "background_mask"
		case "people":
			return "people_mask"
		case "sky":
			return "sky_mask"
		case "brush":
			return "brush_mask"
	}
}
